import { createApp } from 'vue';

import { ApiClient } from './api';
import App from './components/App.vue';
import { normalizeChildrenAges, normalizeConfig } from './config';
import { createState } from './state';
import widgetStyles from './styles.css?inline';
import type {
  ResolvedSearchParams,
  WidgetAccommodation,
  WidgetConfig,
  WidgetError,
  WidgetEventName,
  WidgetInstance,
  WidgetState,
  WidgetTheme,
} from './types';

export function createWidget(rawConfig: WidgetConfig): WidgetInstance {
  const config = normalizeConfig(rawConfig);
  const api = new ApiClient({
    baseUrl: config.apiBaseUrl,
    customerId: config.customerId,
  });

  const state = createState({
    pageSize: config.pageSize,
    cityName: config.initialSearch.cityName ?? null,
    checkIn: config.initialSearch.checkIn ?? undefined,
    checkOut: config.initialSearch.checkOut ?? undefined,
    guests: config.initialSearch.guests,
    children: config.initialSearch.children,
    childrenAges: config.initialSearch.childrenAges,
  });

  const { shadow, host } = mountShadow(config.container);

  // Vue 3 wires `onXxx` root props directly to component `emit('xxx', …)`
  // calls, so we can hand the orchestrator's handlers in as plain props.
  const vueApp = createApp(App, {
    state,
    api,
    showFilter: config.showFilter,
    pagination: config.pagination,
    bookingFlow: config.bookingFlow,
    allowAnyCity: config.allowAnyCity,
    rootStyle: buildRootStyle(config),
    onCloseBooking: () => closeBooking('user'),
    onFilter: (params: ResolvedSearchParams) => {
      void runSearch(params, { reset: true });
    },
    onPage: (page: number) => {
      void runSearch({ ...state.search, page }, { reset: true });
    },
    onLoadMore: () => {
      void runSearch(
        { ...state.search, page: state.page + 1 },
        { reset: false },
      );
    },
    onBook: (item: WidgetAccommodation) => onBookingStart(item),
  });

  vueApp.config.errorHandler = (err): void => {
    publishError({
      code: 'render',
      message: err instanceof Error ? err.message : String(err),
      cause: err,
    });
  };

  let destroyed = false;

  // Страница серча внутри iframe сообщает о шагах флоу и просит закрыть окно.
  const bookingOrigin = new URL(config.bookingBaseUrl).origin;
  window.addEventListener('message', onFrameMessage);

  vueApp.mount(shadow.querySelector('.rh-root') as HTMLElement);

  bootstrap().catch((err) =>
    publishError({
      code: 'bootstrap',
      message: err instanceof Error ? err.message : String(err),
      cause: err,
    }),
  );

  async function bootstrap(): Promise<void> {
    let customer;
    try {
      customer = await api.getCustomerConfig();
      state.customer = customer;
    } catch (err) {
      publishError({
        code: 'customer_lookup',
        message: 'Не удалось загрузить данные клиента',
        cause: err,
      });
      return;
    }

    // Customer exists but is currently hidden from the booking search. Don't
    // start an empty `/search` request — surface the state to the embedder so
    // they can hide/replace the widget instead of showing a blank list.
    if (!customer.bookingSearchEnabled) {
      publishError({
        code: 'customer_disabled',
        message: 'Поиск временно отключён для этого клиента',
      });
      return;
    }

    // Load the host's city list in parallel with the first search. It only
    // feeds the in-widget filter dropdown, so failure here is non-fatal —
    // the filter falls back to free-text autocomplete.
    try {
      const cities = await api.getCustomerCities();
      state.customerCities = cities.map((c) => ({
        id: c.id,
        name: c.name,
        commonCityId: c.common_id ?? c.id,
        tlCityId: c.tl_city_id ?? null,
        rcId: c.rc_id ?? null,
      }));
    } catch {
      state.customerCities = [];
    }

    emit('widget:ready', { customer });
    config.onReady?.({ customer });

    // Гость вернулся с оплаты на страницу хоста (?rh_booking=<id>): сразу
    // показываем подтверждение, а не пустой список.
    resumeBookingFromReturn();

    if (
      !config.showFilter ||
      (config.initialSearch.checkIn && config.initialSearch.cityName)
    ) {
      const initial = state.search;
      // resolve cityId for initial search if only city name was provided
      if (state.search.cityName && !state.search.commonCityId) {
        try {
          const cities = await api.findCity(state.search.cityName);
          const match = cities.find(
            (c) =>
              c.name.toLowerCase() === state.search.cityName!.toLowerCase(),
          );
          if (match) {
            initial.cityId = match.tl_city_id ?? null;
            initial.rcId = match.rc_id ?? null;
            initial.commonCityId = match.common_id ?? match.id;
          }
        } catch {
          // soft-fail: search will report no results
        }
      }
      await runSearch(initial, { reset: true });
    }
  }

  async function runSearch(
    nextSearch: ResolvedSearchParams,
    opts: { reset: boolean },
  ): Promise<void> {
    state.search = { ...nextSearch };
    state.status = opts.reset ? 'loading' : 'loading_more';
    state.error = null;
    emit('search:submitted', { params: { ...state.search } });
    config.onSearch?.({ ...state.search });

    try {
      const { results, total } = await api.search(
        state.search,
        config.mode,
        config.orderBy,
      );
      if (opts.reset) {
        state.results = results;
      } else {
        const seen = new Set(state.results.map((r) => r.id));
        for (const item of results) {
          if (!seen.has(item.id)) state.results.push(item);
        }
      }
      state.total = total;
      state.page = state.search.page;
      state.hasMore = state.results.length < total;
      state.status = state.results.length === 0 ? 'empty' : 'results';
    } catch (err) {
      publishError({
        code: 'search',
        message: 'Не удалось выполнить поиск',
        cause: err,
      });
    }
  }

  function publishError(err: WidgetError): void {
    state.status = 'error';
    state.error = err;
    emit('widget:error', { error: err });
    config.onError?.(err);
  }

  function emit(name: WidgetEventName, detail: Record<string, unknown>): void {
    try {
      host.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    } catch {
      // CustomEvent ctor available in all modern browsers; swallow if not.
    }
  }

  function onBookingStart(item: WidgetAccommodation): void {
    const embedded =
      config.bookingFlow === 'modal' || config.bookingFlow === 'inline';
    const url = buildBookingUrl(item, embedded);
    emit('booking:start', { item, url });
    config.onBookingStart?.(item);
    if (embedded) {
      openBooking(url);
    } else if (config.bookingFlow === 'redirect') {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  function openBooking(url: string): void {
    state.booking = { url, height: null };
    if (config.bookingFlow === 'inline') {
      host.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  function closeBooking(reason: string): void {
    if (!state.booking) return;
    state.booking = null;
    clearReturnParam();
    emit('booking:close', { reason });
    config.onBookingClose?.({ reason });
  }

  // Адрес страницы хоста, куда банк вернёт гостя: без метки прошлой брони и
  // без якоря.
  function resolveReturnUrl(): string {
    const base = config.returnUrl || window.location.href;
    try {
      const target = new URL(base, window.location.href);
      target.searchParams.delete('rh_booking');
      target.hash = '';
      return target.toString();
    } catch {
      return window.location.href;
    }
  }

  function resumeBookingFromReturn(): void {
    if (config.bookingFlow !== 'modal' && config.bookingFlow !== 'inline')
      return;
    const bookingId = new URLSearchParams(window.location.search).get(
      'rh_booking',
    );
    if (!bookingId || !/^[\w-]{1,64}$/.test(bookingId)) return;
    const target = new URL(`${config.bookingBaseUrl}/booking-confirmation`);
    target.searchParams.set('booking_id', bookingId);
    target.searchParams.set('embed', '1');
    target.searchParams.set('return_url', resolveReturnUrl());
    openBooking(target.toString());
  }

  function clearReturnParam(): void {
    try {
      const current = new URL(window.location.href);
      if (!current.searchParams.has('rh_booking')) return;
      current.searchParams.delete('rh_booking');
      window.history.replaceState(window.history.state, '', current.toString());
    } catch {
      // history API недоступен — метка просто останется в адресе
    }
  }

  function onFrameMessage(event: MessageEvent): void {
    if (event.origin !== bookingOrigin) return;
    const data = event.data as { ns?: string; type?: string } & Record<
      string,
      unknown
    >;
    if (!data || data.ns !== 'rh-widget' || !state.booking) return;

    switch (data.type) {
      case 'resize':
        if (typeof data.height === 'number' && data.height > 0) {
          state.booking.height = data.height;
        }
        break;
      case 'close':
        closeBooking(typeof data.reason === 'string' ? data.reason : 'frame');
        break;
      case 'booking_created': {
        const bookingId = String(data.bookingId ?? '');
        emit('booking:created', { bookingId });
        config.onBookingCreated?.({ bookingId });
        break;
      }
      case 'payment_start': {
        const url = typeof data.url === 'string' ? data.url : null;
        emit('payment:start', { url });
        config.onPaymentStart?.({ url });
        break;
      }
      case 'booking_confirmed': {
        const bookingId = data.bookingId ? String(data.bookingId) : null;
        emit('booking:confirmed', { bookingId });
        config.onBookingConfirmed?.({ bookingId });
        break;
      }
      default:
        break;
    }
  }

  function buildBookingUrl(
    item: WidgetAccommodation,
    embedded: boolean,
  ): string {
    // Param names must match what the SPA's /search/:id page reads (see
    // entities/apartment/lib/room-details-query.ts) — arrival_date,
    // departure_date, adults, children, source, common_id. Anything else
    // is silently ignored by the SPA and the detail page falls back to
    // today/today+1, then fails to look the room up.
    const target = new URL(
      `${config.bookingBaseUrl}/search/${encodeURIComponent(item.id)}`,
    );
    const search = state.search;
    if (search.checkIn) target.searchParams.set('arrival_date', search.checkIn);
    if (search.checkOut)
      target.searchParams.set('departure_date', search.checkOut);
    if (search.guests) target.searchParams.set('adults', String(search.guests));

    // Children are sent as comma-separated ages — fall back to age 8 when
    // explicit ages weren't provided, mirroring how /search itself sends
    // placeholders in api.ts.
    const ages =
      search.childrenAges && search.childrenAges.length
        ? search.childrenAges
        : Array.from({ length: search.children || 0 }, () => 8);
    if (ages.length) target.searchParams.set('children', ages.join(','));

    if (search.commonCityId) {
      target.searchParams.set('common_id', String(search.commonCityId));
    }
    if (search.cityId) {
      target.searchParams.set('tl_city_id', String(search.cityId));
    }
    if (search.rcId) {
      target.searchParams.set('rc_id', String(search.rcId));
    }
    target.searchParams.set('source', item.source);
    if (state.customer?.partnerCode) {
      target.searchParams.set('partner', state.customer.partnerCode);
    }
    if (embedded) {
      // Страница серча в iframe: без шапки/футера, оплата с возвратом на сайт хоста.
      target.searchParams.set('embed', '1');
      target.searchParams.set('return_url', resolveReturnUrl());
    }
    return target.toString();
  }

  return {
    updateSearch(params): void {
      const nextAges =
        params.childrenAges !== undefined
          ? normalizeChildrenAges(params.childrenAges)
          : state.search.childrenAges;
      // When childrenAges is set on update, its length wins over `children`.
      const nextChildren =
        params.childrenAges !== undefined
          ? nextAges?.length ?? 0
          : params.children ?? state.search.children;

      const next: ResolvedSearchParams = {
        ...state.search,
        ...(params.city !== undefined ? { cityName: params.city } : {}),
        ...(params.cityId !== undefined ? { cityId: params.cityId } : {}),
        ...(params.rcId !== undefined ? { rcId: params.rcId } : {}),
        ...(params.checkIn ? { checkIn: params.checkIn } : {}),
        ...(params.checkOut ? { checkOut: params.checkOut } : {}),
        ...(params.guests !== undefined ? { guests: params.guests } : {}),
        children: nextChildren,
        childrenAges: nextAges,
        page: 1,
      };
      if (params.city !== undefined && !params.cityId) {
        // resolve cityId asynchronously, then run search
        api
          .findCity(params.city)
          .then((cities) => {
            const match = cities.find(
              (c) => c.name.toLowerCase() === params.city!.toLowerCase(),
            );
            if (match) {
              next.cityId = match.tl_city_id ?? null;
              next.rcId = match.rc_id ?? null;
              next.commonCityId = match.common_id ?? match.id;
            }
            return runSearch(next, { reset: true });
          })
          .catch((err) =>
            publishError({
              code: 'city_lookup',
              message: 'Не удалось определить город',
              cause: err,
            }),
          );
        return;
      }
      void runSearch(next, { reset: true });
    },
    reload(): void {
      void runSearch(state.search, { reset: true });
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      window.removeEventListener('message', onFrameMessage);
      if (state.booking) document.body.style.overflow = '';
      try {
        vueApp.unmount();
      } catch {
        // ignore unmount errors during destroy
      }
      try {
        host.remove();
      } catch {
        // host may already be detached
      }
    },
    getState(): WidgetState {
      return {
        ...state,
        search: { ...state.search },
        results: [...state.results],
      };
    },
  };
}

// Тема и сетка уходят в CSS custom properties корня виджета.
function buildRootStyle(config: {
  theme: WidgetTheme;
  columns: number | null;
}): Record<string, string> {
  const { theme, columns } = config;
  const style: Record<string, string> = {};
  if (theme.accent) {
    style['--rh-color-primary'] = theme.accent;
    style['--rh-color-primary-hover'] = theme.accentHover ?? theme.accent;
    style['--rh-color-primary-soft'] = `${theme.accent}1a`;
  }
  if (theme.accentText) style['--rh-color-on-primary'] = theme.accentText;
  if (theme.fontFamily) style['--rh-font'] = theme.fontFamily;
  if (theme.radius !== undefined) {
    style['--rh-radius-modal'] = `${theme.radius}px`;
    style['--rh-radius-block'] = `${Math.round(theme.radius * 0.6)}px`;
    style['--rh-radius-card-image'] = `${Math.round(theme.radius * 0.5)}px`;
    style['--rh-radius-button'] = `${Math.round(theme.radius * 0.6)}px`;
  }
  if (columns) {
    style['--rh-columns-tpl'] = `repeat(${columns}, minmax(0, 1fr))`;
  }
  return style;
}

function mountShadow(container: HTMLElement): {
  shadow: ShadowRoot;
  host: HTMLElement;
} {
  const host = document.createElement('div');
  host.setAttribute('data-rh-widget', '');
  host.style.display = 'block';
  host.style.width = '100%';
  container.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = widgetStyles;
  shadow.appendChild(style);

  const root = document.createElement('div');
  root.className = 'rh-root';
  shadow.appendChild(root);

  return { shadow, host };
}
